import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { dashboardStream } from "@/lib/ws"
import type { ApprovalOut, ChatTurnOut } from "@/lib/types"
import { Link, Outlet, createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { auth } from "@/lib/auth"
import {
  MessageCircleIcon,
  HistoryIcon,
  SearchIcon,
  ImagesIcon,
  FolderGitIcon,
  BotIcon,
  ZapIcon,
  BookOpenIcon,
  BrainIcon,
  BrainCircuitIcon,
  CalendarClockIcon,
  WandIcon,
  PlugIcon,
  UserIcon,
  UsersIcon,
  CreditCardIcon,
  GaugeIcon,
  SettingsIcon,
  LogOutIcon,
  ShieldIcon,
  ChevronRightIcon,
  MenuIcon,
  XIcon,
} from "lucide-react"
import { useRouterState } from "@tanstack/react-router"
import { syncLocaleFromUser, useT } from "@/lib/i18n"
import { LegalLinks } from "@/components/LegalLinks"
import { SpendMeter } from "@/components/SpendMeter"
import { NotificationBell } from "@/components/NotificationBell"
import { CommandPalette } from "@/components/CommandPalette"
import { OfflineBanner } from "@/components/OfflineBanner"
import { PushNudge } from "@/components/PushNudge"
import { TEAM_INVITE_KEY } from "@/lib/team"

// All in-app routes hang off here. Single auth guard, single shell.
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isLoggedIn()) {
      throw redirect({ to: "/login" })
    }
    if (!context.auth.me()) {
      await context.auth.refreshMe()
    }
    const me = context.auth.me()
    if (!me) {
      throw redirect({ to: "/login" })
    }
    syncLocaleFromUser(me.ui_locale)
    // First-run flow for fresh accounts (admins are exempt). /welcome
    // stamps onboarded_at when it finishes.
    if (!me.onboarded_at && me.tier !== "admin" && !location.pathname.startsWith("/welcome")) {
      throw redirect({ to: "/welcome" })
    }
    // A team invitation opened while signed out is parked in
    // sessionStorage by /team/accept; resume it once we're in.
    let pendingInvite: string | null = null
    try { pendingInvite = sessionStorage.getItem(TEAM_INVITE_KEY) } catch { /* private mode */ }
    if (pendingInvite && !location.pathname.startsWith("/team")) {
      throw redirect({ to: "/team/accept", search: { t: pendingInvite } })
    }
  },
  component: AppShell,
})

function AppShell() {
  const nav = useNavigate()
  const me = auth.me()
  const qc = useQueryClient()
  const t = useT()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const pathname = useRouterState({ select: (st) => st.location.pathname })
  useEffect(() => { setDrawerOpen(false) }, [pathname])

  // Pending-approval count for the sidebar badge — polled + refreshed live.
  const approvalsQ = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => api<ApprovalOut[]>("/api/autonomy/approvals", { query: { status: "pending" } }),
    refetchInterval: 60_000,
    staleTime: 15_000,
  })
  const pendingApprovals = approvalsQ.data?.length ?? 0
  const turnsQ = useQuery({
    queryKey: ["chat-turns", me?.id],
    queryFn: () => api<ChatTurnOut[]>(`/api/dashboard/users/${me?.id}/chat-turns`),
    enabled: !!me?.id,
    refetchInterval: 15_000,
    staleTime: 5_000,
  })
  const activeTurns = turnsQ.data?.length ?? 0
  useEffect(() => dashboardStream.subscribe((evt) => {
    if (["approval_requested", "proposal_created", "autonomy_job_finished"].includes(evt.type)) {
      qc.invalidateQueries({ queryKey: ["approvals"] })
    }
    if (["turn_queued", "turn_started", "turn_completed", "turn_failed", "message"].includes(evt.type)) {
      qc.invalidateQueries({ queryKey: ["chat-turns"] })
      qc.invalidateQueries({ queryKey: ["conversations"] })
    }
  }), [qc])

  async function signOut() {
    await auth.logout()
    await nav({ to: "/login" })
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <CommandPalette />
      <PushNudge />
      {/* The shell is exactly one viewport tall and never scrolls itself:
          the sidebar scrolls on its own if it must, and <main> is the
          one scroller for page content — so the page never "has a
          scrollbar" next to a sidebar that stays put. */}
      {/* Desktop: sticky sidebar. Mobile: top bar + slide-over drawer. */}
      <aside
        className="hidden md:flex w-60 shrink-0 flex-col gap-px px-3.5 pt-4 pb-3.5 [@media(max-height:900px)]:pt-2 [@media(max-height:900px)]:pb-2 h-full overflow-y-auto border-r border-border"
        style={SIDEBAR_STYLE}
      >
        <SidebarContent me={me} activeTurns={activeTurns} pendingApprovals={pendingApprovals} onSignOut={signOut} />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header
          className="md:hidden sticky top-0 z-30 flex h-[52px] shrink-0 items-center gap-3 border-b border-border bg-background/90 px-3 backdrop-blur"
        >
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label={t("nav.openMenu")}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <img src="/lucy-logo.png" alt="" className="h-7 w-7 rounded-full object-cover" />
          <span className="font-display text-[19px]">Lucy</span>
          <span className="ml-auto flex items-center gap-1">
            {(pendingApprovals > 0) && (
              <Link to="/autonomy" className="rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {pendingApprovals}
              </Link>
            )}
            <NotificationBell />
          </span>
        </header>
        <OfflineBanner />

        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
            <aside
              className="absolute inset-y-0 left-0 flex w-[280px] max-w-[85vw] flex-col gap-px overflow-y-auto border-r border-border px-3.5 pt-4 pb-[max(0.875rem,env(safe-area-inset-bottom))] animate-fade-in"
              style={SIDEBAR_STYLE}
            >
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label={t("nav.closeMenu")}
                className="absolute right-2 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
              <SidebarContent me={me} activeTurns={activeTurns} pendingApprovals={pendingApprovals} onSignOut={signOut} />
            </aside>
          </div>
        )}

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full p-4 md:p-8">
          {/* Admin tier never sees this — admins are exempt from the
              email-verification gate. Everyone else gets the prompt
              with real click feedback (Resend used to silently no-op). */}
          {!(me?.email_verified_at || me?.email_verified) && me?.email && me?.tier !== "admin" && (
            <VerifyEmailBanner />
          )}
          <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

const SIDEBAR_STYLE: React.CSSProperties = {
  background: "linear-gradient(180deg, #120C21 0%, #0F0A1C 100%)",
  boxShadow: "inset -1px 0 0 rgba(53,40,90,0.9)",
}

function SidebarContent({
  me, activeTurns, pendingApprovals, onSignOut,
}: {
  me: ReturnType<typeof auth.me>
  activeTurns: number
  pendingApprovals: number
  onSignOut: () => void
}) {
  const t = useT()
  return (
    <>
        {/* Brand */}
        <div className="flex items-center gap-3 px-2 pt-1.5 pb-3 [@media(max-height:900px)]:pb-1.5">
          <img
            src="/lucy-logo.png"
            alt="Lucy"
            className="h-[34px] w-[34px] shrink-0 rounded-full object-cover shadow-[0_4px_14px_rgba(192,140,255,0.45)]"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-display text-[20px] leading-none text-foreground">
              Lucy
            </span>
            <span className="truncate text-[11px] text-muted-foreground">
              {me?.email}
            </span>
          </div>
          <NotificationBell className="ml-auto hidden md:block" />
        </div>

        <nav className="flex flex-col gap-px">
          <GroupLabel>{t("nav.group.talk")}</GroupLabel>
          <NavLink to="/chat" icon={<MessageCircleIcon className="h-[18px] w-[18px]" />} badge={activeTurns}>{t("nav.chat")}</NavLink>
          <NavLink to="/conversations" icon={<HistoryIcon className="h-[18px] w-[18px]" />}>{t("nav.conversations")}</NavLink>
          <NavLink to="/search" icon={<SearchIcon className="h-[18px] w-[18px]" />}>{t("nav.search")}</NavLink>
          <NavLink to="/media" icon={<ImagesIcon className="h-[18px] w-[18px]" />}>{t("nav.media")}</NavLink>
        </nav>

        <nav className="mt-2 flex flex-col gap-px">
          <GroupLabel>{t("nav.group.tools")}</GroupLabel>
          <NavLink to="/projects" icon={<FolderGitIcon className="h-[18px] w-[18px]" />}>{t("nav.projects")}</NavLink>
          <NavLink to="/autonomy" icon={<BotIcon className="h-[18px] w-[18px]" />} badge={pendingApprovals}>{t("nav.autonomy")}</NavLink>
          <NavLink to="/triggers" icon={<ZapIcon className="h-[18px] w-[18px]" />}>{t("nav.triggers")}</NavLink>
          <NavLink to="/knowledge" icon={<BookOpenIcon className="h-[18px] w-[18px]" />}>{t("nav.knowledge")}</NavLink>
          <NavLink to="/memory" icon={<BrainIcon className="h-[18px] w-[18px]" />}>{t("nav.memory")}</NavLink>
          <NavLink to="/brain" icon={<BrainCircuitIcon className="h-[18px] w-[18px]" />}>{t("nav.brain")}</NavLink>
          <NavLink to="/schedules" icon={<CalendarClockIcon className="h-[18px] w-[18px]" />}>{t("nav.schedules")}</NavLink>
          <NavLink to="/prompts" icon={<WandIcon className="h-[18px] w-[18px]" />}>{t("nav.prompts")}</NavLink>
          <NavLink to="/connectors" icon={<PlugIcon className="h-[18px] w-[18px]" />}>{t("nav.connectors")}</NavLink>
        </nav>

        <nav className="mt-2 flex flex-col gap-px">
          <GroupLabel>{t("nav.group.account")}</GroupLabel>
          <NavLink to="/account" icon={<UserIcon className="h-[18px] w-[18px]" />}>{t("nav.account")}</NavLink>
          <NavLink to="/team" icon={<UsersIcon className="h-[18px] w-[18px]" />}>{t("nav.team")}</NavLink>
          <NavLink to="/billing" icon={<CreditCardIcon className="h-[18px] w-[18px]" />}>{t("nav.billing")}</NavLink>
          <NavLink to="/usage" icon={<GaugeIcon className="h-[18px] w-[18px]" />}>{t("nav.usage")}</NavLink>
          <NavLink to="/settings" icon={<SettingsIcon className="h-[18px] w-[18px]" />}>{t("nav.settings")}</NavLink>
        </nav>

        {me?.tier === "admin" && (
          <>
            <a
              href="/admin"
              className="mt-px flex items-center gap-3 rounded-xl px-3 py-[6px] [@media(max-height:900px)]:py-[2px] text-[13.5px] font-medium text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <ShieldIcon className="h-[18px] w-[18px]" />
              <span>{t("nav.operator")}</span>
            </a>
          </>
        )}

        <div className="min-h-3.5 flex-1" />

        {/* Tier / usage card — links to the usage screen. */}
        <Link
          to="/usage"
          className="mb-1.5 flex flex-col gap-2 rounded-[13px] border border-border bg-foreground/[0.04] px-3 py-2.5 [@media(max-height:900px)]:py-2 text-left transition hover:border-gold/25 hover:bg-foreground/[0.06]"
        >
          <div className="flex items-center justify-between gap-2">
            <TierBadge tier={me?.tier ?? "free"} />
            {me?.paused ? (
              <span className="text-[10.5px] text-warn">{t("nav.paused")}</span>
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
          <SpendMeter compact />
        </Link>

        <button
          onClick={onSignOut}
          className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-[14px] font-medium text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
        >
          <LogOutIcon className="h-[18px] w-[18px]" />
          <span>{t("nav.signout")}</span>
        </button>
        <LegalLinks className="pb-1 pt-2" />
    </>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground/80 [@media(max-height:900px)]:pt-1">
      {children}
    </div>
  )
}


function VerifyEmailBanner() {
  const t = useT()
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [err, setErr] = useState<string | null>(null)

  async function resend() {
    setState("sending")
    setErr(null)
    try {
      await auth.resendVerify()
      setState("sent")
    } catch (e) {
      setState("error")
      setErr((e as Error).message)
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-warn/30 bg-warn/10 p-3 text-xs text-warn flex items-center justify-between gap-3">
      <span>
        {state === "sent"
          ? t("verify.sent")
          : state === "error"
          ? `${t("verify.failed")}${err}`
          : t("verify.prompt")}
      </span>
      {state !== "sent" && (
        <button
          className="underline hover:no-underline shrink-0 disabled:opacity-50"
          disabled={state === "sending"}
          onClick={resend}
        >
          {state === "sending" ? t("verify.sending") : t("verify.resend")}
        </button>
      )}
    </div>
  )
}

function TierBadge({ tier }: { tier: string }) {
  // Admin gets the design's glowing-dot gold pill; other tiers a quiet chip.
  const isAdmin = tier === "admin"
  const color = isAdmin
    ? "text-gold border-gold/25 bg-gold/[0.12]"
    : tier === "free"
    ? "text-muted-foreground border-border bg-foreground/[0.04]"
    : "text-mint border-mint/25 bg-mint/[0.12]"
  const dot = isAdmin ? "bg-gold" : tier === "free" ? "bg-muted-foreground" : "bg-mint"
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.06em] ${color}`}
    >
      <span
        className={`h-[5px] w-[5px] rounded-full ${dot}`}
        style={isAdmin ? { boxShadow: "0 0 6px rgba(245,166,35,0.8)" } : undefined}
      />
      {tier}
    </span>
  )
}

function NavLink({
  to, icon, children, badge,
}: {
  to: string
  icon: React.ReactNode
  children: React.ReactNode
  badge?: number
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl px-3 py-[6px] [@media(max-height:900px)]:py-[2px] text-[13.5px] font-medium text-muted-foreground transition hover:bg-secondary/70 hover:text-foreground"
      activeProps={{
        className:
          "bg-secondary text-foreground ring-1 ring-inset ring-border [&>svg]:text-primary",
      }}
    >
      {icon}
      <span className="flex-1">{children}</span>
      {badge ? (
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-[10.5px] font-bold text-black shadow-[0_0_12px_rgba(245,166,35,0.45)]">
          {badge}
        </span>
      ) : null}
    </Link>
  )
}
